import React, {useEffect, useRef, useState} from 'react'
import AdyenCheckout from '@adyen/adyen-web'
import '@adyen/adyen-web/dist/adyen.css'
import {Spinner, Flex} from '@chakra-ui/react'
import PropTypes from 'prop-types'
import {getCurrencyValueForApi} from '@adyenPWA/lib/utils/parsers.mjs'
import {AdyenPaymentsService} from '../../../../../services/adyen/payments'
import {AdyenShippingMethodsService} from '../../../lib/services/shipping-methods'
import useAdyenExpressCheckout from '../../../hooks/useAdyenExpressCheckout'
import {AdyenShippingAddressService} from '@adyenPWA/lib/services/shipping-address'
import useShipToCountry from '../../../hooks/use-ship-to-country'
import {calculateTotalDiscounts} from '../../../utils/basket-utils'
import {reportError} from '../../../utils/utils'
import {useRestartBasket} from '../../../hooks/use-restart-basket'

import {useIntl} from 'react-intl'

/**
 * Extracts the configuration for the Apple Pay payment method from a given payment methods response.
 *
 * @param {Object} paymentMethodsResponse - The response object containing available payment methods.
 * @param {Array} paymentMethodsResponse.paymentMethods - An array of payment method objects.
 * @returns {Object|null} The configuration object for the Apple Pay payment method if available, otherwise null.
 *
 * This function searches through the provided payment methods to find the one with the type 'applepay'.
 * If found, it returns the configuration associated with that payment method. If no such payment method
 * is found, or if the configuration is not available, it returns null.
 */
export const getApplePaymentMethodConfig = (paymentMethodsResponse) => {
    const applePayPaymentMethod = paymentMethodsResponse?.paymentMethods?.find(
        (pm) => pm.type === 'applepay'
    )
    return applePayPaymentMethod?.configuration || null
}

/**
 * Extracts and formats customer shipping details from the provided shipping contact object.
 *
 * @param {Object} shippingContact - The shipping contact information provided by the user.
 * @param {string} shippingContact.locality - The city of the shipping address.
 * @param {string} shippingContact.countryCode - The country code of the shipping address.
 * @param {Array<string>} [shippingContact.addressLines] - The address lines of the shipping address.
 * @param {string} shippingContact.postalCode - The postal code of the shipping address.
 * @param {string} shippingContact.administrativeArea - The state or province of the shipping address.
 * @param {string} shippingContact.givenName - The first name of the customer.
 * @param {string} shippingContact.familyName - The last name of the customer.
 * @param {string} shippingContact.emailAddress - The email address of the customer.
 * @param {string} shippingContact.phoneNumber - The phone number of the customer.
 *
 * @returns {Object} An object containing the formatted delivery address and customer profile.
 * @returns {Object} deliveryAddress - The formatted delivery address.
 * @returns {string} deliveryAddress.city - The city of the delivery address.
 * @returns {string} deliveryAddress.country - The country code of the delivery address.
 * @returns {string} deliveryAddress.houseNumberOrName - The house number or name of the delivery address.
 * @returns {string} deliveryAddress.postalCode - The postal code of the delivery address.
 * @returns {string} deliveryAddress.stateOrProvince - The state or province of the delivery address.
 * @returns {string} deliveryAddress.street - The street of the delivery address.
 * @returns {Object} profile - The customer's profile information.
 * @returns {string} profile.firstName - The first name of the customer.
 * @returns {string} profile.lastName - The last name of the customer.
 * @returns {string} profile.email - The email address of the customer.
 * @returns {string} profile.phone - The phone number of the customer.
 */
export const getCustomerShippingDetails = (shippingContact) => {
    return {
        deliveryAddress: {
            city: shippingContact.locality,
            country: shippingContact.countryCode,
            houseNumberOrName:
                shippingContact.addressLines?.length > 1 ? shippingContact.addressLines[1] : '',
            postalCode: shippingContact.postalCode,
            stateOrProvince: shippingContact.administrativeArea,
            street: shippingContact.addressLines?.[0]
        },
        profile: {
            firstName: shippingContact.givenName,
            lastName: shippingContact.familyName,
            email: shippingContact.emailAddress,
            phone: shippingContact.phoneNumber
        }
    }
}

/**
 * Extracts and formats billing details from a given billing contact object.
 *
 * @param {Object} billingContact - The billing contact information.
 * @param {string} billingContact.locality - The city of the billing address.
 * @param {string} billingContact.countryCode - The country code of the billing address.
 * @param {Array<string>} billingContact.addressLines - The address lines of the billing address.
 * @param {string} billingContact.postalCode - The postal code of the billing address.
 * @param {string} billingContact.administrativeArea - The state or province of the billing address.
 *
 * @returns {Object} An object containing the formatted billing address details.
 * @returns {Object} return.billingAddress - The formatted billing address.
 * @returns {string} return.billingAddress.city - The city of the billing address.
 * @returns {string} return.billingAddress.country - The country code of the billing address.
 * @returns {string} return.billingAddress.houseNumberOrName - The house number or name of the billing address.
 * @returns {string} return.billingAddress.postalCode - The postal code of the billing address.
 * @returns {string} return.billingAddress.stateOrProvince - The state or province of the billing address.
 * @returns {string} return.billingAddress.street - The street of the billing address.
 */
export const getCustomerBillingDetails = (billingContact) => {
    return {
        billingAddress: {
            city: billingContact.locality,
            country: billingContact.countryCode,
            houseNumberOrName:
                billingContact?.addressLines?.length > 1 ? billingContact.addressLines[1] : '',
            postalCode: billingContact.postalCode,
            stateOrProvince: billingContact.administrativeArea,
            street: billingContact.addressLines?.[0]
        }
    }
}

/**
 * Generates an array of additional payment data objects for use with Apple Pay Express checkout.
 *
 * This function takes a shopping basket and a message formatting function as inputs, and returns
 * an array of objects representing additional payment data, such as discounts, tax, and shipping costs.
 * Each object in the array contains a label, amount, and type, which are used to display detailed
 * payment information in the Apple Pay interface.
 *
 * @param {Object} basket - The shopping basket object containing details of the current purchase.
 * @param {Function} formatMessage - A function used to format messages for display, typically for localization purposes.
 * @returns {Array<Object>} An array of objects, each representing a component of the total payment amount.
 * Each object includes:
 *   - {string} label: A formatted string describing the payment component (e.g., "Discount", "Tax", "Shipping").
 *   - {string} amount: The monetary amount associated with the payment component, formatted as a string.
 *   - {string} type: The type of payment component, indicating whether it is a final amount or not.
 */
const getAdditionalPaymentData = (basket, formatMessage) => {
    const additionalPaymentData = []
    const {taxTotal, shippingTotal} = basket
    const discount = calculateTotalDiscounts(basket)
    if (discount !== 0) {
        additionalPaymentData.push({
            label: formatMessage({
                defaultMessage: 'Discount',
                id: 'applepay.modal.totals.discount'
            }),
            amount: `${discount}`,
            type: 'final'
        })
    }

    additionalPaymentData.push({
        label: formatMessage({
            defaultMessage: 'Tax',
            id: 'applepay.modal.totals.tax'
        }),
        amount: `${taxTotal}`,
        type: 'final'
    })

    additionalPaymentData.push({
        label: formatMessage({
            defaultMessage: 'Shipping',
            id: 'applepay.modal.totals.shipping'
        }),
        amount: `${shippingTotal}`
    })

    return additionalPaymentData
}

/**
 * Validates the shipping contact's country against the specified shipping country.
 *
 * This function checks if the country code of the provided shipping contact matches
 * the expected shipping country. If the country codes do not match, it returns an
 * error object indicating that shipping to the selected address is not possible.
 *
 * @param {Object} shippingContact - The shipping contact object containing the country code.
 * @param {Object} shipToCountry - The expected shipping country object, which may contain an 'id' property representing the country code.
 * @param {Function} formatMessage - A function used to format error messages, typically for localization purposes.
 * @returns {Object|undefined} Returns an object containing an array of errors if the country codes do not match, otherwise returns undefined.
 */
const validateShippingContactCountry = (shippingContact, shipToCountry, formatMessage) => {
    if (shippingContact.countryCode !== shipToCountry?.id) {
        return {
            errors: [
                new window.ApplePayError(
                    'addressUnserviceable',
                    'countryCode',
                    formatMessage({
                        defaultMessage: 'Cannot ship to the selected address',
                        id: 'applepay.modal.shipping_address.fail'
                    })
                )
            ]
        }
    }
}

/**
 * Handles errors and resolves the Apple Pay payment request with updated payment details.
 *
 * This function checks for any errors in the payment process and resolves the payment request
 * with updated total amount, line items, and any errors encountered. It is typically used in
 * conjunction with Apple Pay to ensure that the payment details are accurate and any issues
 * are communicated back to the payment sheet.
 *
 * @param {Array} errors - An array of error objects encountered during the payment process.
 * @param {Object} applePayConfig - Configuration object for Apple Pay, containing merchant details.
 * @param {number} applePayAmount - The total amount for the Apple Pay transaction.
 * @param {Object} basket - The current shopping basket containing items and their details.
 * @param {Function} formatMessage - A function to format messages, typically for localization.
 * @param {Function} resolve - A function to resolve the payment request with updated details.
 */
const handleErrorsAndResolve = (
    errors,
    applePayConfig,
    applePayAmount,
    basket,
    formatMessage,
    resolve
) => {
    if (errors.length > 0) {
        resolve({
            newTotal: {
                label: applePayConfig.merchantName,
                amount: `${applePayAmount}`
            },
            newLineItems: getAdditionalPaymentData(basket, formatMessage),
            errors: errors
        })
    }
}

/**
 * Handles the Apple Pay Express checkout process within the shopping cart page.
 *
 * This function is responsible for initiating and managing the Apple Pay Express
 * payment flow. It ensures that the necessary prerequisites for Apple Pay are met,
 * such as checking for device compatibility and availability of payment methods.
 * Once initiated, it manages the interaction with the Apple Pay API, handling
 * user authentication, payment authorization, and transaction completion.
 *
 * The function also manages error handling and provides feedback to the user
 * in case of any issues during the payment process. It ensures a seamless
 * integration with the existing cart and checkout system, updating the cart
 * state and order details upon successful payment.
 *
 * @returns {void}
 */
const handleExceptionError = (errorObject, applePayConfig, applePayAmount, resolve) => {
    reportError('ApplePay', errorObject)

    resolve({
        newTotal: {
            label: applePayConfig.merchantName,
            amount: `${applePayAmount}`
        },
        errors: [new window.ApplePayError('unknown')]
    })
}

export const getAppleButtonConfig = (
    authToken,
    site,
    basket,
    shippingMethods,
    applePayConfig,
    navigate,
    fetchShippingMethods,
    shipToCountry,
    formatMessage,
    restartBasketOrderID
) => {
    // on first cart render basket may have no default shipping method assigned, which will cause orderTotal to be null
    // in this case we will use productTotal as a fallback
    let applePayAmount = basket.orderTotal || basket.productTotal
    const buttonConfig = {
        showPayButton: true,
        isExpress: true,
        configuration: applePayConfig,
        amount: {
            value: getCurrencyValueForApi(applePayAmount, basket.currency),
            currency: basket.currency
        },
        requiredShippingContactFields: ['postalAddress', 'name', 'phoneticName', 'email', 'phone'],
        requiredBillingContactFields: ['postalAddress'],
        shippingMethods: shippingMethods?.map((sm) => ({
            label: sm.name,
            detail: sm.description || sm.name,
            identifier: sm.id,
            amount: `${sm.price}`
        })),
        buttonType: 'check-out',
        buttonColor: 'white-with-line',
        onAuthorized: async (resolve, reject, event) => {
            const errors = []
            try {
                const {shippingContact, billingContact, token} = event.payment

                const state = {
                    data: {
                        paymentType: 'express',
                        paymentMethod: {
                            type: 'applepay',
                            applePayToken: token.paymentData
                        },
                        ...getCustomerBillingDetails(billingContact),
                        ...getCustomerShippingDetails(shippingContact)
                    }
                }
                const adyenPaymentService = new AdyenPaymentsService(authToken, site)
                const paymentsResponse = await adyenPaymentService.submitPayment(
                    {
                        ...state.data,
                        origin: state.data.origin ? state.data.origin : window.location.origin
                    },
                    basket?.basketId,
                    basket?.customerInfo?.customerId
                )

                const shippingErrors = validateShippingContactCountry(
                    shippingContact,
                    shipToCountry,
                    formatMessage
                )

                if (shippingErrors) {
                    errors.push(...shippingErrors.errors)
                }

                handleErrorsAndResolve(
                    errors,
                    applePayConfig,
                    applePayAmount,
                    basket,
                    formatMessage,
                    resolve
                )
                if (paymentsResponse?.isFinal && paymentsResponse?.isSuccessful) {
                    const finalPriceUpdate = {
                        newTotal: {
                            type: 'final',
                            label: applePayConfig.merchantName,
                            amount: `${applePayAmount}`
                        },
                        newLineItems: getAdditionalPaymentData(basket, formatMessage)
                    }
                    resolve(finalPriceUpdate)
                    navigate(`/checkout/confirmation/${paymentsResponse?.merchantReference}`)
                } else {
                    await restartBasketOrderID(paymentsResponse?.merchantReference)
                    return reject()
                }
            } catch (err) {
                try {
                    const res = JSON.parse(err.message)
                    const orderNo = res?.orderNo
                    if (orderNo) {
                        await restartBasketOrderID(orderNo)
                        return reject()
                    }
                } catch (parseError) {
                    console.error('Failed to parse error message:', parseError)
                }

                handleExceptionError(err, applePayConfig, applePayAmount, resolve)
            }
        },
        onSubmit: () => {},
        onShippingContactSelected: async (resolve, reject, event) => {
            const errors = []

            try {
                const {shippingContact} = event
                const adyenShippingAddressService = new AdyenShippingAddressService(authToken, site)
                const adyenShippingMethodsService = new AdyenShippingMethodsService(authToken, site)
                const customerShippingDetails = getCustomerShippingDetails(shippingContact)
                await adyenShippingAddressService.updateShippingAddress(
                    basket.basketId,
                    customerShippingDetails
                )

                const shippingErrors = validateShippingContactCountry(
                    shippingContact,
                    shipToCountry,
                    formatMessage
                )

                if (shippingErrors) {
                    errors.push(...shippingErrors.errors)
                }
                const newShippingMethods = await fetchShippingMethods(
                    basket?.basketId,
                    site,
                    authToken,
                    basket?.customerInfo?.customerId
                )

                handleErrorsAndResolve(
                    errors,
                    applePayConfig,
                    applePayAmount,
                    basket,
                    formatMessage,
                    resolve
                )
                if (!newShippingMethods?.applicableShippingMethods?.length) {
                    errors.push(
                        new window.ApplePayError(
                            'shippingContactInvalid',
                            'postalAddress',
                            formatMessage({
                                defaultMessage: 'No applicable shipping methods available.',
                                id: 'applepay.shipping.fail'
                            })
                        )
                    )
                    handleErrorsAndResolve(
                        errors,
                        applePayConfig,
                        applePayAmount,
                        basket,
                        formatMessage,
                        resolve
                    )
                } else {
                    const response = await adyenShippingMethodsService.updateShippingMethod(
                        newShippingMethods.applicableShippingMethods[0].id,
                        basket.basketId,
                        basket?.customerInfo?.customerId
                    )
                    buttonConfig.amount = {
                        value: getCurrencyValueForApi(response.orderTotal, response.currency),
                        currency: response.currency
                    }
                    applePayAmount = response.orderTotal

                    if (site?.id.includes('US') && !response?.c_taxValid) {
                        const taxError = new window.ApplePayError(
                            'shippingContactInvalid',
                            'countryCode',
                            formatMessage({
                                defaultMessage:
                                    'Cannot ship to the selected address - tax calculation failed',
                                id: 'applepay.modal.taxcalculation.fail'
                            })
                        )
                        errors.push(taxError)
                    }

                    handleErrorsAndResolve(
                        errors,
                        applePayConfig,
                        applePayAmount,
                        basket,
                        formatMessage,
                        resolve
                    )

                    const finalPriceUpdate = {
                        newShippingMethods: newShippingMethods?.applicableShippingMethods?.map(
                            (sm) => ({
                                label: sm.name,
                                detail: sm.description || sm.name,
                                identifier: sm.id,
                                amount: `${sm.price}`
                            })
                        ),
                        newTotal: {
                            type: 'final',
                            label: applePayConfig.merchantName,
                            amount: `${applePayAmount}`
                        },
                        newLineItems: getAdditionalPaymentData(response, formatMessage)
                    }

                    resolve(finalPriceUpdate)
                }
            } catch (err) {
                handleExceptionError(err, applePayConfig, applePayAmount, resolve)
            }
        },
        onShippingMethodSelected: async (resolve, reject, event) => {
            try {
                const {shippingMethod} = event
                const adyenShippingMethodsService = new AdyenShippingMethodsService(authToken, site)
                const response = await adyenShippingMethodsService.updateShippingMethod(
                    shippingMethod.identifier,
                    basket.basketId,
                    basket?.customerInfo?.customerId
                )
                if (response.error) {
                    handleExceptionError(response, applePayConfig, applePayAmount, resolve)
                } else {
                    buttonConfig.amount = {
                        value: getCurrencyValueForApi(response.orderTotal, response.currency),
                        currency: response.currency
                    }
                    applePayAmount = response.orderTotal
                    const applePayShippingMethodUpdate = {
                        newTotal: {
                            type: 'final',
                            label: applePayConfig.merchantName,
                            amount: `${applePayAmount}`
                        },
                        newLineItems: getAdditionalPaymentData(response, formatMessage)
                    }
                    resolve(applePayShippingMethodUpdate)
                }
            } catch (err) {
                handleExceptionError(err, applePayConfig, applePayAmount, resolve)
            }
        }
    }
    return buttonConfig
}

const ApplePayExpressComponent = (props) => {
    const {
        adyenEnvironment,
        adyenPaymentMethods,
        basket,
        locale,
        site,
        authToken,
        navigate,
        shippingMethods,
        fetchShippingMethods
    } = useAdyenExpressCheckout()
    const paymentContainer = useRef(null)
    const [applePayButton, setApplePayButton] = useState(null)
    const {shipToCountry} = useShipToCountry()
    const {formatMessage} = useIntl()
    const {restartBasketOrderID} = useRestartBasket()

    useEffect(() => {
        const createCheckout = async () => {
            try {
                const checkout = await AdyenCheckout({
                    environment: adyenEnvironment?.ADYEN_ENVIRONMENT,
                    clientKey: adyenEnvironment?.ADYEN_CLIENT_KEY,
                    locale: locale.id,
                    analytics: {
                        analyticsData: {
                            applicationInfo: adyenPaymentMethods?.applicationInfo
                        }
                    }
                })
                const applePaymentMethodConfig = getApplePaymentMethodConfig(adyenPaymentMethods)
                // apple pay is not available for current cart, no need to proceed
                if (!applePaymentMethodConfig) {
                    return
                }
                const appleButtonConfig = getAppleButtonConfig(
                    authToken,
                    site,
                    basket,
                    shippingMethods?.applicableShippingMethods,
                    applePaymentMethodConfig,
                    navigate,
                    fetchShippingMethods,
                    shipToCountry,
                    formatMessage,
                    restartBasketOrderID
                )
                const button = await checkout.create('applepay', appleButtonConfig)
                const isApplePayButtonAvailable = await button.isAvailable()
                if (isApplePayButtonAvailable) {
                    button.mount(paymentContainer.current)
                    setApplePayButton(button)
                }
            } catch (err) {
                console.log(err)
            }
        }
        if (
            adyenEnvironment &&
            adyenPaymentMethods &&
            basket &&
            shippingMethods &&
            paymentContainer.current
        ) {
            createCheckout()
        }

        // Cleanup function to unmount the component
        return () => {
            if (applePayButton) {
                applePayButton.unmount()
            }
        }
    }, [adyenEnvironment, adyenPaymentMethods, basket, shippingMethods])

    return (
        <>
            {props.showLoading && (
                <Flex align={'center'} justify={'center'}>
                    <Spinner size={'lg'} mt={4} />
                </Flex>
            )}
            <div ref={paymentContainer}></div>
        </>
    )
}

ApplePayExpressComponent.propTypes = {
    showLoading: PropTypes.bool,
    shippingMethods: PropTypes.array
}

export default ApplePayExpressComponent
